import { useEffect, useState } from "react";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import { API_ROUTES } from "../../../../core/Constants";
import { getUSA, getUser, useHub, useScreen } from "../../../../core/Global";
import { HereGeocodeItem, HereGeocodePosition } from "../../../../models/MapModels";
import { toastSuccess } from "../../../../components/base/Toast";
import FlexRow from "../../../../components/base/FlexRow";
import ColorPicker from "../../../../components/form/ColorPicker";
import TextBox from "../../../../components/form/TextBox";
import Select from "../../../../components/form/Select";
import Country from "../../../base/models/Country";
import PhoneNumberSelect from "../../../../components/form/PhoneNumberSelect";
import MetroEditMap from "../../../../components/map/MetroEditMap";
import AddressField from "../../../../components/here/AddressField";
import Tagify from "../../../../components/tagify/Tagify";
import JobMetro, { JobMetroLayer } from "../models/JobMetro";
import TextArea from "../../../../components/form/TextArea";
import TechPoolSelector from "../components/TechPoolSelector";

type IProps = {
  metro?: JobMetro
  onClose: () => void
  onSubmit: () => void
}

const DRAWER_ID = "drawer-metro-form";

// A country in the Country picker: its flag, then its name.
const renderCountry = (c: Country) => (
  <span className="d-flex align-items-center min-w-0">
    {c.flagSquare && <img src={c.flagSquare} alt="" className="rounded-1 me-3 flex-shrink-0" width={20} height={20} />}
    <span className="text-truncate">{c.name}</span>
  </span>
);

/**
 * Create or edit a metro, like the technician drawer: the form in the drawer -- colour, name,
 * description, phone, country, state and the technician pool -- and the area-drawing map beside
 * it, full height, from the drawer's edge to the screen's, with an address search over it. On a
 * phone there is no room beside it, so the map sits in the form, under country and state.
 */
export default function MetroFormDrawer(props: IProps) {

  const hub = useHub();
  const user = getUser();
  const usa = getUSA();

  const screen = useScreen();

  // The map is made once the drawer is open, as in the technician drawer: made while the
  // drawer is still hidden, Leaflet measures nothing and stays blank.
  const [opened, openedSet] = useState<boolean>(false)

  // An address searched for under the state: the map goes to it.
  const [mapFocus, mapFocusSet] = useState<HereGeocodeItem>()

  const [layersMap, layersMapSet] = useState<JobMetroLayer[]>([])
  const [centerMap, centerMapSet] = useState<HereGeocodePosition>()
  const [zoomMap, zoomMapSet] = useState<number>()

  // PhoneNumberSelect posts no form control of its own, so the chosen line is held here and
  // merged into the body on submit. Empty means "no override", so it is not preselected.
  const [forceLineId, forceLineIdSet] = useState<string | undefined>(props.metro?.forceProxyLineId)

  // Known from the first render: the map opens with them, and takes a later change of either
  // as the user's pick to go to.
  const [countryId, countryIdSet] = useState<string | undefined>(props.metro ? props.metro.countryId : usa.id)
  const [stateId, stateIdSet] = useState<string | undefined>(props.metro?.stateId)

  const [techIds, techIdsSet] = useState<string[]>()
  const [priorityTechIds, priorityTechIdsSet] = useState<string[]>()

  useEffect(() => {
    if (props.metro?.techIds) {
      techIdsSet(props.metro.techIds)
    }
    else techIdsSet([])
  }, [props.metro?.techIds])

  const [countries, countriesSet] = useState<Country[]>()
  useEffect(() => {
    hub.Get(API_ROUTES.CountriesAllActive, res => {
      if (res.success) countriesSet(res.data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [states, statesSet] = useState<any[]>()
  const fetchStates = () => {
    hub.Get(API_ROUTES.States + "?countryId=" + countryId, res => {
      if (res.success) statesSet(res.data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  const [clearSearch, clearSearchSet] = useState<boolean>(false)
  useEffect(() => {
    if (clearSearch) {
      setTimeout(() => {
        clearSearchSet(false);
      }, 1000);
    }
  }, [clearSearch])

  useEffect(() => {
    if (props.metro) {
      countryIdSet(props.metro.countryId)
      stateIdSet(props.metro.stateId)
      layersMapSet(props.metro.layers ?? [])
      zoomMapSet(props.metro.mapZoom)
      centerMapSet(props.metro.mapCenter)
    }
    else {
      countryIdSet(usa.id)
    }
  }, [props.metro])

  useEffect(() => {
    if (countryId) fetchStates()
  }, [countryId])

  // The map, with the address search over its top left (the drawing tools are top right).
  const renderMap = (height: number) => (
    <div className="position-relative">
      <MetroEditMap height={height} metro={props.metro}
        countryId={countryId} stateId={stateId} focus={mapFocus}
        onLayersChanged={layers => layersMapSet(layers)}
        onZoomChanged={zoom => zoomMapSet(zoom)}
        onCenterChanged={center => centerMapSet(center)} />
      <div className="position-absolute shadow-sm rounded"
        style={{ top: 12, left: 12, width: "min(440px, calc(100% - 84px))", zIndex: 1000 }}>
        {/* Kept to the picked country, and to the state when there is one. */}
        <AddressField id="txt-metro-map-search" name="mapSearch" raw reset={clearSearch}
          countryId={countryId} stateId={stateId}
          placeholder={stateId ? "Search an address in this state..." : "Search an address..."}
          onSelect={item => mapFocusSet(item)} />
      </div>
    </div>
  );

  return (
    <DrawerForm id={DRAWER_ID}
      title={props.metro ? "Edit Metro" : "Create Metro"}
      subtitle={props.metro?.name}
      // Wider than a medium drawer on big screens, about as wide as the map beside it; on a
      // laptop it is only a little wider. Drawer + map always leave a strip of the page showing.
      width={`${Math.min(Math.round(screen.width * .45), 960)}px`}
      buttonText={props.metro ? "Update Metro" : "Create Metro"}
      sidePanelOpen={!screen.isMobile}
      sidePanelWidth={`${Math.min(Math.round(screen.width * .5), 960)}px`}
      sidePanel={!screen.isMobile && opened && renderMap(screen.height)}
      onOpen={() => {
        openedSet(true)
        setTimeout(() => $("#txt-addmetro-name").focus(), 100)
      }}
      onClose={props.onClose}
      onSubmit={(body, callback) => {

        var bad = false;

        if (!body.name || body.name === "" || body.name.length < 2) {
          window.formError("name", "Please write a valid name!", DRAWER_ID, true);
          bad = true;
        }

        if (window.isNullOrEmpty(body.countryId)) {
          window.formError("countryId", "Please choose the metro country!", DRAWER_ID);
          bad = true;
        }

        if (window.isNullOrEmpty(body.stateId)) {
          window.formError("stateId", "Please choose the metro state/region!", DRAWER_ID);
          bad = true;
        }

        if (bad) {
          if (callback) callback();
          return;
        }

        hub.Post(API_ROUTES.JobMetros, {
          ...body,
          forceLineId: forceLineId,
          id: props.metro?.id,
          layers: layersMap,
          mapCenter: centerMap,
          mapZoom: zoomMap,
          techIds: techIds,
          priorityTechIds: priorityTechIds
        }, res => {
          if (res.success) {
            props.onSubmit();
            toastSuccess("Job metro has been saved successfully!");
          }
          if (callback) callback(res);
        })

      }}>

      <FlexRow className="mb-5">
        <ColorPicker id="color-add-metro" width={30} className="me-2" name="color" color={props.metro?.color} />
        <div className="flex-grow-1">
          Pick Metro Color
        </div>
      </FlexRow>
      {
        user?.isSuperAdmin &&
        <>
          <div className="fs-7 fw-bold">
            Assign to
            <span id="ownerUserId-err" className="small text-danger mx-2 form-error"></span>
          </div>
          <Tagify name="ownerUserId" id="drp-ownerUserId" placeholder="Select a Customer" apiUrl={API_ROUTES.TagifyCustomers} single block />
        </>
      }

      <TextBox id="txt-addmetro-name" name="name" block label="Name" labelUp marginBottom={3} placeholder="Metro Name" value={props.metro?.name} />

      <TextArea id="txt-metro-descr" name="description" block label="Description" labelUp marginBottom={3} rows={4} placeholder="Write about the metro..." value={props.metro?.description} />

      <PhoneNumberSelect name="forceLineId" label="Default Phone for Extensions" labelUp block deselect
        value={forceLineId} onChange={forceLineIdSet} />

      <div className="separator my-6"></div>

      <div className="row g-4">
        <div className="col-6">
          <Select id="drp-addmetro-country" label="Country" name="countryId" labelUp marginBottom={0}
            source={countries} value={countryId}
            optionTemplate={renderCountry} selectionTemplate={renderCountry}
            onSelectItem={(c: Country) => {
              if (!c?.id || c.id === countryId) return
              // Another country: the state picked belonged to the old one.
              stateIdSet(undefined)
              countryIdSet(c.id)
              clearSearchSet(true)
            }} />
        </div>
        <div className="col-6">
          <Select id="drp-addmetro-state" label="State" name="stateId" labelUp marginBottom={0} deselect
            source={states} value={stateId}
            onSelectItem={item => {
              if (!item?.id || item.id === stateId) return
              stateIdSet(item.id)
              clearSearchSet(true)
            }}
            onClear={() => {
              stateIdSet(undefined)
              clearSearchSet(true)
            }} />
        </div>
      </div>

      {screen.isMobile && opened && <div className="mt-5">{renderMap(400)}</div>}

      <div className="separator my-6"></div>

      <TechPoolSelector selectedTechIds={props.metro?.techIds}
        priorityTechIds={props.metro?.priorityTechIds}
        onChange={ids => techIdsSet(ids)}
        onPriorityChange={ids => priorityTechIdsSet(ids)} />

    </DrawerForm>
  )
}
